import { useCallback, useEffect, useRef, useState } from "react";
import Field from "./components/field/Field";
import Greeting from "./components/greeting/Greeting";
import Scoreboard from "./components/scoreboard/Scoreboard";
import { GiGoalKeeper } from "react-icons/gi";
import "./index.css";
import { io } from "socket.io-client";
import whistle from "../src/assets/whistle.wav";
import kickSound0 from "../src/assets/ball-kick0.wav";
import kickSound1 from "../src/assets/ball-kick1.wav";
import winSound from "../src/assets/win-cartoon.mp3";
import lossSound from "../src/assets/loss-cartoon.mp3";
import { randomStringGenerator } from "./helpers/randomStrGenerator";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:8777";
const socket = io(SOCKET_URL, { autoConnect: false });

function directionToKey(direction) {
    if (direction === 0) return "left";
    if (direction === 1) return "up";
    return "right";
}

export default function App() {
    const [chartArr, setchartArr] = useState([]);
    const [isGameOver, setIsGameOver] = useState(false);
    const [isItAfterShot, setIsItAfterShot] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isRoleSwitched, setIsRoleSwitched] = useState(false);
    const [isWaitingUser, setIsWaitingUser] = useState(true);
    const [message, setMessage] = useState("Choose a side to shoot!");
    const [moveBallTo, setMoveBallTo] = useState("");
    const [moveGKTo, setmoveGKTo] = useState("");
    const [score, setScore] = useState(0);
    // change name to scoreComp
    const [scorePC, setScorePC] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [kickSound, setKickSound] = useState(false);
    const [isConnected, setIsConnected] = useState(socket.connected);
    const [matchId, setMatchId] = useState("");
    const [myId, setMyId] = useState("");
    const [myLabel, setMyLabel] = useState("you");
    const [opponentLabel, setOpponentLabel] = useState("opponent");
    const [keeperFaceClass, setKeeperFaceClass] = useState("gk-head-1");

    const isPlayingRef = useRef(false);
    const isGameOverRef = useRef(false);
    const isAnimatingRef = useRef(false);
    const animationTimeoutRef = useRef(null);
    const queuedMessageRef = useRef("");
    const queuedKeeperFaceRef = useRef("gk-head-1");
    const playerNameRef = useRef("");
    const myIdRef = useRef("");

    useEffect(() => {
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    useEffect(() => {
        isGameOverRef.current = isGameOver;
    }, [isGameOver]);

    useEffect(() => {
        myIdRef.current = myId;
    }, [myId]);

    function playKickSound() {
        const sound = kickSound ? kickSound0 : kickSound1;
        new Audio(sound).play();
        setKickSound(!kickSound);
    }

    function playWinSound() {
        setTimeout(() => {
            new Audio(winSound).play();
        }, 500);
    }

    function playLossSound() {
        setTimeout(() => {
            new Audio(lossSound).play();
        }, 500);
    }

    function playWhistle() {
        new Audio(whistle).play();
    }

    const applyState = useCallback((state) => {
        if (!state || !state.players || state.players.length < 2) {
            return;
        }

        const meByFlag = state.players.find((player) => player.isYou);
        const meById = state.players.find(
            (player) => player.id === myIdRef.current
        );
        const me = meByFlag || meById || state.players[0];
        const opponent =
            state.players.find((player) => player.id !== me.id) || me;

        setMyId(me.id);
        setMyLabel(me.name || "you");
        setOpponentLabel(opponent.name || "opponent");
        setScore(me.score ?? 0);
        setScorePC(opponent.score ?? 0);
        setchartArr(state.chartArr || []);

        const currentTurn = state.currentTurn || {};
        const isMyTurn =
            currentTurn.shooterId === me.id || currentTurn.keeperId === me.id;
        const amKeeper = currentTurn.keeperId === me.id;
        const submittedBy = currentTurn.submittedBy || [];
        const hasSubmitted = submittedBy.includes(me.id);
        const firstPlayerId = state.players[0]?.id;
        const secondPlayerId = state.players[1]?.id;
        let nextKeeperFaceClass = "gk-head-1";

        if (currentTurn.keeperId === firstPlayerId) {
            nextKeeperFaceClass = "gk-head-1";
        } else if (currentTurn.keeperId === secondPlayerId) {
            nextKeeperFaceClass = "gk-head-2";
        }

        setIsRoleSwitched(amKeeper);
        setIsWaitingUser(state.status === "playing" && isMyTurn && !hasSubmitted);

        let nextMessage = "Waiting for opponent...";
        if (state.status === "finished") {
            setIsGameOver(true);
            setIsWaitingUser(false);
            nextMessage = "Game over!";
        } else {
            setIsGameOver(false);
            if (isMyTurn && !hasSubmitted) {
                if (submittedBy.length > 0) {
                    nextMessage = "Opponent chose. Make your move!";
                } else {
                    nextMessage = amKeeper
                        ? "Choose a side to jump!"
                        : "Choose a side to shoot!";
                }
            } else {
                nextMessage = "Choice locked. Waiting for opponent...";
            }
        }

        queuedMessageRef.current = nextMessage;
        queuedKeeperFaceRef.current = nextKeeperFaceClass;
        if (!isAnimatingRef.current) {
            setKeeperFaceClass(nextKeeperFaceClass);
        }
        if (!isAnimatingRef.current) {
            setMessage(nextMessage);
        }
    }, []);

    function resetGame() {
        if (animationTimeoutRef.current) {
            clearTimeout(animationTimeoutRef.current);
            animationTimeoutRef.current = null;
        }
        isAnimatingRef.current = false;
        setchartArr([]);
        setMessage("Choose a side to shoot!");
        setmoveGKTo("");
        setMoveBallTo("");
        setScore(0);
        setScorePC(0);
        setIsItAfterShot(false);
        setIsWaitingUser(true);
        setIsRoleSwitched(false);
        setKickSound(false);
        setMatchId("");
        setMyId("");
        setMyLabel("you");
        setOpponentLabel("opponent");
        setKeeperFaceClass("gk-head-1");
        queuedKeeperFaceRef.current = "gk-head-1";
    }

    function joinQueue() {
        const name = playerNameRef.current;
        socket.emit("queue:join", { name }, (response) => {
            if (!response || !response.ok) {
                setMessage("Could not join queue. Please try again.");
                return;
            }
            setMessage("Waiting for opponent...");
        });
    }

    useEffect(() => {
        function handleConnect() {
            setIsConnected(true);
            if (isPlayingRef.current) {
                joinQueue();
            }
        }

        function handleDisconnect() {
            setIsConnected(false);
            if (isPlayingRef.current && !isGameOverRef.current) {
                setMessage("Disconnected from server.");
                setIsGameOver(true);
                setIsWaitingUser(false);
                playLossSound();
            }
        }

        function handleConnectError(error) {
            setIsConnected(false);
            setMessage(
                `Connection failed: ${error?.message || "unknown error"}`
            );
        }

        function handleQueueWaiting() {
            setMessage("Waiting for opponent...");
        }

        function handleMatchFound(payload) {
            setMatchId(payload.matchId || "");
            setMyId(payload.youId || "");
            setOpponentLabel(payload.opponent?.name || "opponent");
            setMessage("Match found. Get ready!");
            playWhistle();
        }

        function handleMatchState(payload) {
            applyState(payload);
        }

        function handleTurnResolved(payload) {
            isAnimatingRef.current = true;
            setIsItAfterShot(true);
            setIsWaitingUser(false);
            setMoveBallTo(directionToKey(payload.shooterChoice));
            setmoveGKTo(directionToKey(payload.keeperChoice));

            if (payload.isGoal) {
                setMessage("⚽️  GOAL  ⚽️");
            } else {
                setMessage(
                    <>
                        <GiGoalKeeper size={26} style={{ color: "#fff" }} /> SAVED{" "}
                        <GiGoalKeeper size={26} style={{ color: "#fff" }} />
                    </>
                );
            }

            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
            animationTimeoutRef.current = setTimeout(() => {
                isAnimatingRef.current = false;
                setIsItAfterShot(false);
                setKeeperFaceClass(queuedKeeperFaceRef.current || "gk-head-1");
                setMessage(queuedMessageRef.current || "Waiting for opponent...");
            }, 900);
        }

        function handleMatchEnded(payload) {
            if (payload?.state) {
                applyState(payload.state);
            }
            setIsGameOver(true);
            setIsWaitingUser(false);
            if (payload?.winnerId && payload.winnerId === myIdRef.current) {
                playWinSound();
            } else {
                playLossSound();
            }
        }

        function handleServerError(payload) {
            setMessage(payload?.message || "Server error.");
        }

        socket.on("connect", handleConnect);
        socket.on("connect_error", handleConnectError);
        socket.on("disconnect", handleDisconnect);
        socket.on("queue:waiting", handleQueueWaiting);
        socket.on("match:found", handleMatchFound);
        socket.on("match:state", handleMatchState);
        socket.on("turn:resolved", handleTurnResolved);
        socket.on("match:ended", handleMatchEnded);
        socket.on("server:error", handleServerError);

        return () => {
            socket.off("connect", handleConnect);
            socket.off("connect_error", handleConnectError);
            socket.off("disconnect", handleDisconnect);
            socket.off("queue:waiting", handleQueueWaiting);
            socket.off("match:found", handleMatchFound);
            socket.off("match:state", handleMatchState);
            socket.off("turn:resolved", handleTurnResolved);
            socket.off("match:ended", handleMatchEnded);
            socket.off("server:error", handleServerError);
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [applyState]);

    function handleArrowClick(e) {
        if (!isConnected || isGameOver || !isWaitingUser) {
            return;
        }

        const choice = Number(e.currentTarget.value);
        if (!Number.isInteger(choice) || choice < 0 || choice > 2) {
            return;
        }

        playKickSound();
        setIsWaitingUser(false);
        setMessage("Choice locked. Waiting for opponent...");

        socket.emit("turn:submit", { matchId, choice }, (response) => {
            if (!response?.ok) {
                setIsWaitingUser(true);
                setMessage("Your move did not reach server. Try again.");
            }
        });
    }

    function startGame() {
        resetGame();
        setIsPlaying(true);
        setIsGameOver(false);
        setShowModal(false);
        setMessage("Connecting to server...");

        const storedName = localStorage.getItem("risky-kick-player-name");
        const name = storedName || `Player-${randomStringGenerator(5)}`;
        localStorage.setItem("risky-kick-player-name", name);
        playerNameRef.current = name;

        if (!socket.connected) {
            socket.connect();
        } else {
            joinQueue();
        }
    }

    function handleShowModal() {
        setShowModal(true);
    }

    function handleNo() {
        isPlayingRef.current = false;
        setIsPlaying(false);
        socket.emit("match:leave", () => {});
        socket.emit("queue:leave", () => {});
        if (socket.connected) {
            socket.disconnect();
        }
        setIsConnected(false);
        setIsGameOver(true);
        setShowModal(false);
        resetGame();
    }

    function handleYes() {
        resetGame();
        setShowModal(false);
        startGame();
    }

    return (
        <div>
            {isPlaying ? (
                <>
                    <Scoreboard
                        chartArr={chartArr}
                        opponentLabel={opponentLabel}
                        score={score}
                        scorePC={scorePC}
                        userLabel={myLabel}
                    />
                    <Field
                        handleNo={handleNo}
                        handleShowModal={handleShowModal}
                        handleYes={handleYes}
                        isGameOver={isGameOver}
                        isItAfterShot={isItAfterShot}
                        isRoleSwitched={isRoleSwitched}
                        keeperFaceClass={keeperFaceClass}
                        isWaitingUser={isWaitingUser}
                        message={message}
                        moveBallTo={moveBallTo}
                        moveGKTo={moveGKTo}
                        onArrowClick={handleArrowClick}
                        score={score}
                        scorePC={scorePC}
                        showModal={showModal}
                    />
                </>
            ) : (
                <Greeting startGame={startGame} />
            )}
        </div>
    );
}
